import type { Product as FrontendProduct } from "@/hooks/use-shop-data";
import type { SortOption } from "@/utils/routes";

// Infer the Shopify Product type from the oRPC client
// Structural subset of the Shopify product shape used by transformShopifyProduct
type ShopifyProduct = {
  id: string;
  handle: string;
  title: string;
  description: string;
  productType?: string; // optional to align with API variability
  vendor: string;
  tags?: string[];
  availableForSale: boolean;
  createdAt?: string;
  images?: FrontendProduct["images"];
  options?: FrontendProduct["options"];
  priceRange: {
    minVariantPrice: { amount: string; currencyCode: string };
    maxVariantPrice: { amount: string; currencyCode: string };
  };
  variants: Array<{
    id: string;
    title: string;
    price: { amount: string; currencyCode: string };
    compareAtPrice?: { amount: string; currencyCode: string } | null;
    availableForSale: boolean;
    quantityAvailable: number | null;
    selectedOptions: Array<{ name: string; value: string }>;
    image?: {
      id: string;
      url: string;
      altText: string | null;
      width: number | null;
      height: number | null;
    } | null;
  }>;
};

/**
 * Transform Shopify Product to Frontend Product interface
 * Maps Shopify data structure to the format expected by the frontend components
 */
export function transformShopifyProduct(
  shopifyProduct: ShopifyProduct
): FrontendProduct {
  // Extract price from the first variant or price range
  const price =
    shopifyProduct.variants.length > 0
      ? Number.parseFloat(shopifyProduct.variants[0].price.amount)
      : Number.parseFloat(shopifyProduct.priceRange.minVariantPrice.amount);

  // Map Shopify product type to frontend category
  const category = mapShopifyCategory(
    shopifyProduct.productType || "",
    shopifyProduct.tags || []
  );

  // Map Shopify vendor to frontend brand slug
  const brand = mapShopifyBrand(shopifyProduct.vendor);

  // Determine if product is in stock
  const inStock =
    shopifyProduct.availableForSale &&
    shopifyProduct.variants.some((variant) => variant.availableForSale);

  // Check if product is featured (based on tags or other criteria)
  const featured = Boolean(shopifyProduct.tags?.includes("featured"));

  // Parse creation date with deterministic fallback (avoid dynamic Date() which can cause hydration mismatch)
  const createdAt = new Date(
    shopifyProduct.createdAt || "1970-01-01T00:00:00.000Z"
  );

  // Deterministic pseudo-random sales figure derived from product id so SSR & CSR match
  const sales = (() => {
    const id = shopifyProduct.id || "unknown";
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      hash = (hash * 31 + id.charCodeAt(i)) >>> 0; // simple deterministic hash
    }
    const min = 100;
    const max = 1100; // exclusive upper bound for range size 1000
    return min + (hash % (max - min));
  })();

  // Use direct options from Shopify (they're working correctly)
  const options = shopifyProduct.options || [];

  return {
    id: shopifyProduct.id,
    handle: shopifyProduct.handle,
    name: shopifyProduct.title,
    description: shopifyProduct.description,
    price,
    category,
    brand,
    inStock,
    featured,
    createdAt,
    sales,
    images: shopifyProduct.images || [], // Include images from Shopify
    // Enhanced variant and option support
    variants: shopifyProduct.variants || [],
    options,
    priceRange: shopifyProduct.priceRange,
  };
}

/**
 * Map Shopify product type and tags to frontend category
 */
export function mapShopifyCategory(
  productType: string,
  tags: string[]
): string {
  // Normalize the product type for flexible matching
  const normalizedProductType = productType.toLowerCase().trim();

  // Fixed category mapping - more direct and accurate
  const categoryMappings = [
    {
      keywords: ["protein", "whey", "casein", "isolate"],
      category: "Protein",
    },
    {
      keywords: ["pre-workout", "preworkout", "pre workout", "energy", "pump"],
      category: "Pre-Workout",
    },
    {
      keywords: ["vitamin", "multivitamin", "mineral"],
      category: "Vitamins",
    },
    {
      keywords: ["creatine", "monohydrate"],
      category: "Creatine",
    },
    {
      keywords: ["fat burn", "fat-burn", "thermogenic", "weight loss", "diet"],
      category: "Fat Burners",
    },
    {
      keywords: ["recovery", "bcaa", "amino", "glutamine", "post-workout"],
      category: "Recovery",
    },
    {
      keywords: ["performance", "strength", "endurance"],
      category: "Performance",
    },
  ];

  // First try to map by product type using flexible keyword matching
  if (normalizedProductType) {
    for (const mapping of categoryMappings) {
      if (
        mapping.keywords.some((keyword) =>
          normalizedProductType.includes(keyword)
        )
      ) {
        return mapping.category;
      }
    }
  }

  // Then try to map by tags using the same flexible approach
  for (const tag of tags) {
    const normalizedTag = tag.toLowerCase().trim();
    for (const mapping of categoryMappings) {
      if (mapping.keywords.some((keyword) => normalizedTag.includes(keyword))) {
        return mapping.category;
      }
    }
  }

  // If we have a product type but no match, use it as-is (capitalized)
  if (productType?.trim()) {
    const capitalizedType = productType
      .trim()
      .replace(/\b\w/g, (l) => l.toUpperCase());
    return capitalizedType;
  }

  // Default fallback
  return "Supplements";
}

/**
 * Map Shopify vendor to frontend brand slug
 */
function mapShopifyBrand(vendor: string): string {
  if (!vendor?.trim()) {
    return "unknown";
  }

  // Normalize vendor name for flexible matching
  const normalizedVendor = vendor.trim();

  // Enhanced brand mapping with case-insensitive matching
  const brandMap: Record<string, string> = {
    "optimum nutrition": "optimum-nutrition",
    myprotein: "myprotein",
    grenade: "grenade",
    bulk: "bulk",
    "bulk powders": "bulk",
    usn: "usn",
    "applied nutrition": "applied-nutrition",
  };

  // Try exact match first (case-insensitive)
  const exactMatch = brandMap[normalizedVendor.toLowerCase()];
  if (exactMatch) {
    return exactMatch;
  }

  // Try partial matching for common variations
  const lowerVendor = normalizedVendor.toLowerCase();
  for (const [key, value] of Object.entries(brandMap)) {
    if (lowerVendor.includes(key) || key.includes(lowerVendor)) {
      return value;
    }
  }

  // Default: convert to slug format
  return normalizedVendor
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

/**
 * Map frontend sort option to Shopify sort parameters
 */
export function mapSortToShopify(sort: SortOption): {
  sortKey?: string;
  reverse?: boolean;
} {
  const sortMap: Record<SortOption, { sortKey?: string; reverse?: boolean }> = {
    featured: { sortKey: "RELEVANCE" },
    "price-low-high": { sortKey: "PRICE", reverse: false },
    "price-high-low": { sortKey: "PRICE", reverse: true },
    newest: { sortKey: "CREATED_AT", reverse: true },
    "best-selling": { sortKey: "BEST_SELLING" },
    alphabetical: { sortKey: "TITLE", reverse: false },
  };

  return sortMap[sort] || { sortKey: "RELEVANCE" };
}

// Note: Category and brand mapping is now handled entirely in the backend
// buildShopifyQuery function for consistency between server and client rendering

/**
 * Transform Shopify PageInfo to include total count estimation
 * Note: Shopify doesn't provide total counts, so we estimate based on pagination
 */
export function transformPageInfo(
  pageInfo: { hasNextPage: boolean; hasPreviousPage: boolean },
  currentProducts: number,
  currentPage: number
) {
  // Estimate total products (this is a limitation of Shopify's API)
  // In a real implementation, you might cache this or use a different approach
  const estimatedTotal =
    currentPage === 1 && !pageInfo.hasNextPage
      ? currentProducts
      : currentProducts * (currentPage + (pageInfo.hasNextPage ? 2 : 0));

  const productsPerPage = 9; // Should match PRODUCTS_PER_PAGE constant
  const estimatedTotalPages = Math.ceil(estimatedTotal / productsPerPage);

  return {
    totalProducts: estimatedTotal,
    totalPages: estimatedTotalPages,
    currentPage,
    hasNextPage: pageInfo.hasNextPage,
    hasPreviousPage: pageInfo.hasPreviousPage,
  };
}
